import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { http } from "../../../axios/axios";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";

const InputField = ({
  label,
  name,
  type = "text",
  placeholder,
  register,
  errors = {},
  validation = {},
  as = "input",
  rows = 4,
  options = [],
}) => {
  const inputClass =
    "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm md:px-4 md:py-3 md:text-base outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block text-sm font-semibold text-slate-700 md:mb-2 md:text-base"
      >
        {label}
        {validation.required && <span className="text-red-500"> *</span>}
      </label>

      {as === "textarea" ? (
        <textarea
          id={name}
          rows={rows}
          placeholder={placeholder}
          {...register(name, validation)}
          className={`${inputClass} resize-none`}
        />
      ) : as === "select" ? (
        <select
          id={name}
          {...register(name, validation)}
          className={inputClass}
        >
          <option value="">{placeholder}</option>

          {options.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          type={type}
          placeholder={placeholder}
          {...register(name, validation)}
          className={inputClass}
        />
      )}

      {errors[name] && (
        <p className="mt-1 text-xs text-red-500 md:text-sm">
          {errors[name].message}
        </p>
      )}
    </div>
  );
};

export default function Contact() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);

  const contactOptions = [
    { value: "phone", label: "Phone" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "email", label: "Email" },
  ];

  const branches = [
    {
      id: "delhi-1",
      city: "Delhi HQ",
      address:
        "Plot No - A-1 & 2, Shop No-4 & 5, First Floor, Chanakya Place, opp. Mata Chanan Devi Hospital, Janakpuri, Delhi 110058",
      mapSrc:
        "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3402.590945473216!2d77.07747824630212!3d28.617322273728863!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d054add5c1ce1%3A0x5672c6a05146e0d0!2sSuraksha24x7!5e0!3m2!1sen!2sin!4v1776057615946!5m2!1sen!2sin",
    },
  ];

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const res = await http.post("contact", data);

      toast.success(res.data.message);
      reset();

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to submit contact request.",
      );
    } finally {
      setLoading(false);
    }
  };

  const [seo, setSeo] = useState(null);

  useEffect(() => {
    fetchSeo();
  }, []);

  const fetchSeo = async () => {
    try {
      const { data } = await http.get("/seo/Contact");
      setSeo(data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const seoData = seo || {
    metaTitle: "",
    metaDescription: "",
    metaTags: [],
    schemaScript: null,
  };

  const schemaScript = seoData?.schemaScript
    ? typeof seoData.schemaScript === "string"
      ? seoData.schemaScript
      : JSON.stringify(seoData.schemaScript)
    : null;

  return (
    <>
      <Helmet>
        <title>{seoData?.metaTitle}</title>

        <meta name="description" content={seoData?.metaDescription} />

        <meta
          name="keywords"
          content={
            Array.isArray(seoData?.metaTags)
              ? seoData?.metaTags.join(", ")
              : seoData?.metaTags
          }
        />

        {schemaScript && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: schemaScript }}
          />
        )}
      </Helmet>

      <div className="min-h-screen bg-white pb-20 font-sans">
        <header className="bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 py-20 px-6 text-center relative overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight font-serif">
              Contact <span className="text-blue-400">Us</span>
            </h1>

            <p className="hidden md:block mt-6 text-lg text-gray-300 leading-relaxed">
              We're here to help. Whether you have a general question, need
              support, want to discuss a partnership, or require assistance with
              our services, our team is just a message away.
            </p>
          </div>
        </header>

        <section className="px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start ">
            <div className="rounded-md border border-gray-200 bg-white px-5 py-6 sm:px-6 sm:py-8 lg:p-8 md:col-span-2">
              <h2 className="text-2xl md:text-3xl font-bold font-serif text-slate-900">
                Get in Touch
              </h2>

              <p className="mt-3 text-sm md:text-base text-gray-600 leading-7">
                We're here to answer your questions and assist you with any
                general inquiries.
              </p>

              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <Phone className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Call Us</h3>
                    <p className="text-sm md:text-base text-gray-600">
                      +91 844-834-1881
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Mail className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Email Us</h3>
                    <div className="flex items-start flex-col  gap-1 overflow-auto">
                      <p className="text-sm md:text-base text-gray-600 break-all">
                        support@suraksha24x7.com
                      </p>
                      <p className="text-sm md:text-base text-gray-600 break-all">
                        info@suraksha24x7.com
                      </p>
                      <p className="text-sm md:text-base text-gray-600 break-all">
                        hr@suraksha24x7.com
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Business Hours
                    </h3>
                    <p className="text-sm md:text-base text-gray-600">
                      Monday – Saturday
                      <br />
                      9:00 AM – 7:00 PM
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Phone className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Support Helpline
                    </h3>
                    <p className="text-sm md:text-base text-gray-600">
                      +91 844-834-1881
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Office Address
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 leading-7">
                      Plot No - A-1 & 2, Shop No-4 & 5, First Floor, Chanakya
                      Place, Opp. Mata Chanan Devi Hospital, Janakpuri, Delhi –
                      110058
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-blue-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">
                      Our Presence
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 leading-7">
                      Delhi, Haryana, Uttar Pradesh, Mumbai, Punjab, Bihar,
                      Jharkhand, Kolkata, Madhya Pradesh, Bengaluru, Rajasthan,
                      Assam, Hyderabad, Uttarakhand
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-md border border-gray-200 bg-white  px-4 py-8 lg:p-8 md:col-span-3">
              <h2 className="text-2xl md:text-3xl font-bold font-serif text-slate-900">
                Submit a Complaint
              </h2>

              <p className="mt-3 text-sm md:text-base text-gray-600 leading-7">
                Please provide the required information below.
              </p>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="mt-8 space-y-6"
              >
                <InputField
                  label="Full Name"
                  name="name"
                  placeholder="e.g., Rahul Sharma"
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Full name is required",
                  }}
                />

                <InputField
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  placeholder="e.g., 9876543210"
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Phone number is required",
                    pattern: {
                      value: /^[6-9]\d{9}$/,
                      message: "Enter a valid phone number",
                    },
                  }}
                />

                <InputField
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="e.g., rahul.sharma@example.com"
                  register={register}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField
                    label="City"
                    name="city"
                    placeholder="e.g., New Delhi"
                    register={register}
                    errors={errors}
                    validation={{
                      required: "City is required",
                    }}
                  />

                  <InputField
                    label="State"
                    name="state"
                    placeholder="e.g., Delhi"
                    register={register}
                    errors={errors}
                    validation={{
                      required: "State is required",
                    }}
                  />
                </div>

                <InputField
                  label="Subject"
                  name="subject"
                  placeholder="e.g., CCTV Installation Inquiry"
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Subject is required",
                  }}
                />
                <InputField
                  label="Preferred Contact Method"
                  name="contactMethod"
                  as="select"
                  placeholder="Select a contact method"
                  options={contactOptions}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Please select a contact method",
                  }}
                />
                <InputField
                  label="Message"
                  name="message"
                  as="textarea"
                  rows={5}
                  placeholder="Tell us about your requirements, such as the property type, number of cameras needed, installation location, or any questions you have."
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Please enter your message",
                  }}
                />

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-md bg-blue-600 px-6 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>

        <section className="bg-white/[0.02] border-y border-white/5 py-12 md:py-16">
          <div className="px-4 sm:px-6 lg:px-10">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <MapPin className="h-7 w-7 text-blue-600 shrink-0" />

              <h2 className="text-2xl md:text-3xl font-bold font-serif text-slate-900">
                Find us on Map
              </h2>
            </div>
            {console.log(branches)}

            <div className="grid grid-cols-1 gap-6">
              {branches.map((branch) => (
                <div
                  key={branch.id}
                  className="overflow-hidden rounded-2xl shadow-lg border border-gray-200 bg-white"
                >
                  <div className="p-5 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {branch.city}
                    </h3>

                    <p className="mt-2 text-sm text-gray-600 leading-6">
                      {branch.address}
                    </p>
                  </div>

                  <iframe
                    title={branch.city}
                    src={branch.mapSrc}
                    className="w-full h-[280px] sm:h-[350px] md:h-[450px] lg:h-[550px]"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
